import { MediaType } from '@/types/media';
import type { Media as MediaShape, VideoMedia } from '@/types/media';

import LazyVideo from '../LazyVideo';
import LazyPhoto from '../LazyPhoto';
import MediaCarousel from '../MediaCarousel';
import { PlaybackMode } from '@/types/media';
import { TEST_IDS } from './constants/testIds';

const DEFAULT_MEDIA_SIZES = '(max-width: 640px) 35vw, (max-width: 1024px) 30vw, (max-width: 1440px) 14vw, (max-width: 1920px) 14rem, 22rem';

export type MediaItemInput =
  | {
      type: typeof MediaType.IMAGE;
      media: MediaShape;
      title: string;
      blurDataUrl?: string;
      /**
       * Every preview a multi-output preset holds. More than one and the tile becomes the same
       * subdivided frame the generated run gets, so a preset looks the same wherever it appears.
       * Handled here rather than per surface — grid, hub, search, seasonal, trending and the
       * preset hero all render through this component.
       */
      mediaList?: MediaShape[];
    }
  | {
      type: typeof MediaType.VIDEO;
      media: VideoMedia;
      title: string;
      /**
       * One entry per output. Every surface cycles them, and the slide on screen plays its clip
       * — the carousel keeps only that one live, so a card costs a single decoder.
       */
      mediaList?: VideoMedia[];
    };

type MediaProps = {
  item: MediaItemInput;
  priority?: boolean;
  className?: string;
  mediaClassName?: string;
  sizes?: string;
  playbackMode?: PlaybackMode;
  enableAudioControls?: boolean;
};

const Media = ({ item, priority, className, mediaClassName, sizes = DEFAULT_MEDIA_SIZES, playbackMode = PlaybackMode.Scheduled, enableAudioControls = false }: MediaProps) => {
  const { media, title, type } = item;
  if (!media) {
    return null;
  }

  const { width, height, url } = media;
  // `${width}/${height}` is only a valid aspect-ratio when both are real numbers. A media row
  // saved without dimensions would yield `0/0`, which the browser drops — and the carousel frame
  // is `height: 100%`, so inside an auto-height grid cell it would collapse to nothing. Fall back
  // to the frame's own square rather than an invisible tile.
  const aspectRatio = width > 0 && height > 0 ? `${width}/${height}` : undefined;

  switch (type) {
    case MediaType.VIDEO: {
      // A multi-output video preset cycles its previews rather than showing one and a count, and
      // the slides play — the carousel keeps only the visible one live, so a card costs one
      // decoder however many outputs it has, and a page of them costs one each.
      if (item.mediaList && item.mediaList.length > 1) {
        // Deduped on the clip, not the poster: two outputs can share one poster and still be two
        // different previews.
        const cells = item.mediaList
          .filter(({ poster }) => Boolean(poster))
          .filter((entry, index, all) => all.findIndex(other => other.url === entry.url) === index)
          .map(entry => ({ ...entry, url: entry.poster, videoUrl: entry.url }));
        if (cells.length > 1) {
          return <MediaCarousel images={cells} title={title} className={className} mediaClassName={mediaClassName} sizes={sizes} priority={priority} aspectRatio={aspectRatio} />;
        }
      }

      return (
        <LazyVideo
          src={url}
          priority={priority}
          title={title}
          poster={media.poster}
          width={width}
          height={height}
          className={className}
          mediaClassName={mediaClassName}
          sizes={sizes}
          playbackMode={playbackMode}
          enableAudioControls={enableAudioControls}
        />
      );
    }
    case MediaType.IMAGE: {
      // A CMS item can list the same output twice, and cycling between identical slides reads as a
      // stalled tile. The miniapp drops the repeats too (`getPresetPreviews`), so a preset looks the
      // same in both.
      const images = item.mediaList?.filter((entry, index, all) => all.findIndex(other => other.url === entry.url) === index) ?? [];
      if (images.length > 1) {
        return <MediaCarousel images={images} title={title} className={className} mediaClassName={mediaClassName} sizes={sizes} priority={priority} aspectRatio={aspectRatio} />;
      }

      return (
        <LazyPhoto
          src={url}
          width={width}
          height={height}
          title={title}
          alt={title}
          blurDataUrl={item?.blurDataUrl}
          priority={priority}
          className={className}
          mediaClassName={mediaClassName}
          dataTestId={TEST_IDS.PHOTO}
          sizes={sizes}
        />
      );
    }
    default:
      return null;
  }
};

export default Media;
